import { useState, useMemo } from 'react'
import { FileText, Upload, AlertTriangle, DollarSign, Calendar, Filter, RefreshCw, Download, Search, Plus, Eye, Edit, CheckCircle, Clock, XCircle, AlertCircle, X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { contractsService } from '../services/contracts'
import DocumentUpload from '../components/DocumentUpload'
import { toast } from 'react-hot-toast'

export default function ContractsPage() {
  const [selectedProject] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [selectedType, setSelectedType] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedContractId] = useState<string | null>(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingContract, setViewingContract] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  // Contract creation form state
  const [newContract, setNewContract] = useState({
    name: '',
    contract_type: '',
    party_a_name: '',
    party_a_type: 'owner',
    party_b_name: '',
    party_b_type: 'contractor',
    contract_value: '',
    start_date: '',
    end_date: '',
    project_id: ''
  })

  // Fetch contracts
  const { data: contracts, isLoading, error, refetch } = useQuery({
    queryKey: ['contracts', selectedProject],
    queryFn: async () => {
      if (selectedProject === 'all') {
        // Fetch all contracts for the organization
        return await contractsService.getAllContracts()
      }
      return await contractsService.getProjectContracts(selectedProject)
    },
    refetchInterval: 60000, // Refresh every minute
  })

  // Filter contracts
  const filteredContracts = useMemo(() => {
    if (!contracts) return []
    
    let filtered = [...contracts]
    
    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(c => c.status === selectedStatus)
    }
    
    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(c => c.contract_type === selectedType)
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(term) ||
        c.party_a_name.toLowerCase().includes(term) ||
        c.party_b_name.toLowerCase().includes(term)
      )
    }
    
    return filtered
  }, [contracts, selectedStatus, selectedType, searchTerm])

  // Calculate statistics
  const stats = useMemo(() => {
    if (!contracts) return {
      total: 0,
      active: 0,
      underReview: 0,
      totalValue: 0,
      avgRiskScore: 0,
      highRiskCount: 0
    }

    const active = contracts.filter(c => c.status === 'active').length
    const underReview = contracts.filter(c => c.status === 'under_review').length
    const totalValue = contracts.reduce((sum, c) => sum + (c.contract_value || 0), 0)
    const contractsWithRisk = contracts.filter(c => c.risk_score !== undefined && c.risk_score !== null)
    const avgRiskScore = contractsWithRisk.length > 0
      ? Math.round(contractsWithRisk.reduce((sum, c) => sum + (c.risk_score || 0), 0) / contractsWithRisk.length)
      : 0
    const highRiskCount = contracts.filter(c => (c.risk_score || 0) > 60).length

    return {
      total: contracts.length,
      active,
      underReview,
      totalValue,
      avgRiskScore,
      highRiskCount
    }
  }, [contracts])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'draft':
        return <Edit className="h-5 w-5 text-gray-500" />
      case 'under_review':
        return <Clock className="h-5 w-5 text-yellow-500" />
      case 'expired':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'terminated':
        return <AlertCircle className="h-5 w-5 text-red-600" />
      default:
        return <FileText className="h-5 w-5 text-gray-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'draft':
        return 'bg-gray-100 text-gray-800'
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800'
      case 'expired':
        return 'bg-red-100 text-red-800'
      case 'terminated':
        return 'bg-red-200 text-red-900'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRiskColor = (score: number | undefined) => {
    if (!score) return 'text-gray-400'
    if (score <= 30) return 'text-green-600'
    if (score <= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getRiskLabel = (score: number | undefined) => {
    if (!score) return 'Not Assessed'
    if (score <= 30) return 'Low Risk'
    if (score <= 60) return 'Medium Risk'
    return 'High Risk'
  }

  const formatCurrency = (value: number | undefined) => {
    if (!value) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const handleAnalyzeContract = async (contractId: string) => {
    try {
      await contractsService.analyzeContract(contractId)
      refetch()
    } catch (error) {
      console.error('Failed to analyze contract:', error)
    }
  }

  const handleDownloadContract = async (contract: any) => {
    // Generate the full contract content
    const contractContent = `CONSTRUCTION CONTRACT AGREEMENT

Contract Number: ${contract.id}
Contract Name: ${contract.name}
Contract Type: ${contract.contract_type}
Date: ${new Date().toLocaleDateString()}

==================================================
PARTIES TO THE AGREEMENT
==================================================

Party A (Contractor):
${contract.parties?.[0] || 'ABC Construction Inc.'}
Address: 123 Builder Lane, Construction City, ST 12345
License #: CON-2024-1234
Contact: John Smith, Project Manager
Email: john.smith@abcconstruction.com
Phone: (555) 123-4567

Party B (Owner):
${contract.parties?.[1] || 'XYZ Property Owners'}
Address: 456 Property Ave, Real Estate City, ST 67890
Contact: Jane Doe, Property Owner
Email: jane.doe@xyzproperties.com
Phone: (555) 987-6543

==================================================
PROJECT DETAILS
==================================================

Project Name: ${contract.project_name || 'Downtown Tower Development'}
Project Location: ${contract.location || '789 Main Street, Metro City, ST 11111'}
Project Type: Commercial Construction
Total Square Footage: 50,000 sq ft
Number of Floors: 12

==================================================
CONTRACT VALUE AND PAYMENT TERMS
==================================================

Total Contract Value: ${formatCurrency(contract.value)}
Payment Schedule:
- 20% upon contract signing
- 30% upon foundation completion
- 30% upon structure completion
- 15% upon interior finishing
- 5% upon final inspection and approval

Payment Terms: Net 30 days from invoice date
Late Payment Interest: 1.5% per month

==================================================
SCOPE OF WORK
==================================================

This agreement covers the complete construction services including:

1. SITE PREPARATION
   - Demolition of existing structures
   - Site excavation and grading
   - Installation of temporary facilities
   - Environmental protection measures

2. FOUNDATION WORK
   - Soil testing and preparation
   - Foundation excavation
   - Concrete footings and foundation walls
   - Waterproofing and drainage systems

3. STRUCTURAL CONSTRUCTION
   - Steel frame erection
   - Concrete floor slabs
   - Exterior wall systems
   - Roof structure and covering

4. MECHANICAL, ELECTRICAL, PLUMBING (MEP)
   - HVAC system installation
   - Electrical distribution and lighting
   - Plumbing and fixtures
   - Fire protection systems

5. INTERIOR FINISHING
   - Drywall and partitions
   - Flooring installation
   - Painting and wall finishes
   - Doors and hardware

6. EXTERNAL WORK
   - Landscaping
   - Parking areas
   - Sidewalks and external lighting
   - Signage

==================================================
TIMELINE AND MILESTONES
==================================================

Project Start Date: ${contract.start_date ? new Date(contract.start_date).toLocaleDateString() : 'March 1, 2024'}
Project End Date: ${contract.end_date ? new Date(contract.end_date).toLocaleDateString() : 'December 31, 2024'}

Key Milestones:
- Site Preparation Complete: April 15, 2024
- Foundation Complete: May 31, 2024
- Structure Complete: August 31, 2024
- MEP Systems Complete: October 15, 2024
- Interior Finishing Complete: November 30, 2024
- Final Inspection: December 15, 2024

==================================================
TERMS AND CONDITIONS
==================================================

1. CHANGES AND MODIFICATIONS
   All changes must be documented in writing and approved by both parties.

2. WARRANTIES
   Contractor warrants all work for a period of one (1) year from completion.

3. INSURANCE REQUIREMENTS
   - General Liability: Minimum $2,000,000
   - Workers' Compensation: As required by state law
   - Builder's Risk: Full replacement value

4. PERFORMANCE BOND
   Required - 10% of total contract value

5. DISPUTE RESOLUTION
   Any disputes shall first attempt resolution through mediation.

6. TERMINATION
   Either party may terminate with 30 days written notice.

7. FORCE MAJEURE
   Neither party liable for delays due to circumstances beyond control.

==================================================
COMPLIANCE AND RISK ASSESSMENT
==================================================

Risk Level: ${getRiskLabel(contract.risk_score)}
Compliance Score: ${contract.compliance_score || 92}%
Last Compliance Check: ${new Date().toLocaleDateString()}

Key Risk Factors:
- Payment terms: LOW RISK
- Timeline feasibility: MEDIUM RISK
- Regulatory compliance: LOW RISK
- Subcontractor management: MEDIUM RISK

==================================================
SIGNATURES
==================================================

CONTRACTOR:                        OWNER:

_________________________         _________________________
Authorized Signature              Authorized Signature

_________________________         _________________________
Print Name                        Print Name

_________________________         _________________________
Title                            Title

_________________________         _________________________
Date                             Date

==================================================
ATTACHMENTS
==================================================

A. Detailed Project Plans and Specifications
B. Payment Schedule Breakdown
C. Insurance Certificates
D. Licenses and Permits
E. Subcontractor List

This is a sample contract document for demonstration purposes.
Actual contracts should be reviewed by legal counsel before signing.
`

    try {
      // Check if the File System Access API is available (Chrome/Edge)
      if ('showSaveFilePicker' in window) {
        const options = {
          suggestedName: `${contract.name.replace(/\s+/g, '_')}_Contract.txt`,
          types: [
            {
              description: 'Text Document',
              accept: {
                'text/plain': ['.txt'],
              },
            },
            {
              description: 'PDF Document',
              accept: {
                'application/pdf': ['.pdf'],
              },
            },
            {
              description: 'Word Document',
              accept: {
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
              },
            },
          ],
        }

        // Show save file dialog
        const handle = await (window as any).showSaveFilePicker(options)
        const writable = await handle.createWritable()

        // Write the contract content
        await writable.write(contractContent)
        await writable.close()

        // Show success notification
        toast.success('Contract downloaded successfully to your selected location!')
      } else {
        // Fallback for browsers that don't support File System Access API
        // Create a more user-friendly download with confirmation
        const fileName = prompt(
          'Enter filename for the contract:',
          `${contract.name.replace(/\s+/g, '_')}_Contract.txt`
        )

        if (fileName) {
          const blob = new Blob([contractContent], { type: 'text/plain;charset=utf-8' })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName.endsWith('.txt') ? fileName : `${fileName}.txt`

          // Style the link for better visibility
          link.style.display = 'none'
          document.body.appendChild(link)

          // Trigger download
          link.click()

          // Clean up
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          // Show success message
          setTimeout(() => {
            toast.success(`Contract "${fileName}" has been downloaded to your default Downloads folder.`)
          }, 500)
        }
      }
    } catch (error) {
      // Handle errors or user cancellation
      if ((error as any).name !== 'AbortError') {
        console.error('Error downloading contract:', error)
        toast.error('Error downloading contract. Please try again.')
      }
    }
  }

  const handleExportContracts = () => {
    if (!filteredContracts || filteredContracts.length === 0) {
      toast.error('No contracts to export')
      return
    }

    try {
      // Create CSV content
      const headers = ['Name', 'Type', 'Status', 'Party A', 'Party B', 'Value', 'Start Date', 'End Date']
      const csvRows = [
        headers.join(','),
        ...filteredContracts.map(contract => [
          `"${contract.name || ''}"`,
          `"${contract.contract_type || ''}"`,
          `"${contract.status || ''}"`,
          `"${contract.party_a_name || ''}"`,
          `"${contract.party_b_name || ''}"`,
          contract.contract_value || '',
          contract.start_date || '',
          contract.end_date || ''
        ].join(','))
      ]

      const csvContent = csvRows.join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `contracts-export-${Date.now()}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast.success(`Exported ${filteredContracts.length} contracts successfully!`)
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export contracts. Please try again.')
    }
  }

  const handleCreateContract = async () => {
    // Validation
    if (!newContract.name || !newContract.contract_type || !newContract.party_a_name || !newContract.party_b_name || !newContract.project_id) {
      toast.error('Please fill in all required fields (Name, Type, Parties, and Project)')
      return
    }

    try {
      setLoading(true)

      await contractsService.createContract({
        project_id: newContract.project_id,
        name: newContract.name,
        contract_type: newContract.contract_type,
        party_a_name: newContract.party_a_name,
        party_a_type: newContract.party_a_type,
        party_b_name: newContract.party_b_name,
        party_b_type: newContract.party_b_type,
        contract_value: newContract.contract_value ? parseFloat(newContract.contract_value) : undefined,
        start_date: newContract.start_date || undefined,
        end_date: newContract.end_date || undefined,
      })

      toast.success('Contract created successfully!')
      setShowCreateModal(false)
      setNewContract({
        name: '',
        contract_type: '',
        party_a_name: '',
        party_a_type: 'owner',
        party_b_name: '',
        party_b_type: 'contractor',
        contract_value: '',
        start_date: '',
        end_date: '',
        project_id: ''
      })
      refetch()
    } catch (error) {
      console.error('Failed to create contract:', error)
      toast.error('Failed to create contract. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleUploadFiles = async (files: File[]) => {
    if (!selectedContractId) {
      toast.error('No contract selected for upload')
      return
    }

    try {
      setLoading(true)

      // Upload each file
      for (const file of files) {
        await contractsService.uploadContract(selectedContractId, file)
      }

      toast.success(`Successfully uploaded ${files.length} file(s)`)
      setShowUploadModal(false)
      refetch()
    } catch (error) {
      console.error('Failed to upload files:', error)
      toast.error('Failed to upload contract files. Please try again.')
      throw error
    } finally {
      setLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 font-semibold">Failed to load contracts</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contract Management</h1>
            <p className="mt-2 text-gray-600">Manage and analyze construction contracts for risk and compliance</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => refetch()}
              className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button
              onClick={handleExportContracts}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Contract
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Contract
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Contracts</p>
              <p className="text-2xl font-bold mt-1">{stats.total}</p>
            </div>
            <FileText className="h-8 w-8 text-blue-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold mt-1 text-green-600">{stats.active}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Under Review</p>
              <p className="text-2xl font-bold mt-1 text-yellow-600">{stats.underReview}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-xl font-bold mt-1">{formatCurrency(stats.totalValue)}</p>
            </div>
            <DollarSign className="h-8 w-8 text-green-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Risk Score</p>
              <p className={`text-2xl font-bold mt-1 ${getRiskColor(stats.avgRiskScore)}`}>
                {stats.avgRiskScore}%
              </p>
            </div>
            <AlertTriangle className="h-8 w-8 text-orange-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">High Risk</p>
              <p className="text-2xl font-bold mt-1 text-red-600">{stats.highRiskCount}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-200" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Filter className="h-4 w-4 text-gray-400 mr-2" />
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="border-gray-300 rounded-md text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="under_review">Under Review</option>
                  <option value="expired">Expired</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Types</option>
                <option value="Construction">Construction</option>
                <option value="Subcontract">Subcontract</option>
                <option value="Design-Build">Design-Build</option>
                <option value="Supply">Supply</option>
                <option value="Service">Service</option>
              </select>
            </div>
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search contracts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Contracts List */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contract
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parties
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContracts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    No contracts found
                  </td>
                </tr>
              ) : (
                filteredContracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(contract.status)}
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">{contract.name}</p>
                          <p className="text-sm text-gray-500">{contract.contract_type}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm text-gray-900">{contract.party_a_name}</p>
                        <p className="text-sm text-gray-500">↓</p>
                        <p className="text-sm text-gray-900">{contract.party_b_name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(contract.contract_value)}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                        {contract.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-sm font-medium ${getRiskColor(contract.risk_score)}`}>
                          {contract.risk_score ? `${contract.risk_score}%` : '-'}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          {getRiskLabel(contract.risk_score)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contract.start_date && (
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                            <span>{new Date(contract.start_date).toLocaleDateString()}</span>
                          </div>
                        )}
                        {contract.end_date && (
                          <div className="text-xs text-gray-500">
                            to {new Date(contract.end_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => {
                            setViewingContract(contract)
                            setShowViewModal(true)
                          }}
                          className="text-primary-600 hover:text-primary-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {!contract.risk_score && (
                          <button
                            onClick={() => handleAnalyzeContract(contract.id)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Analyze Risk"
                          >
                            <AlertTriangle className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDownloadContract(contract)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Risk Summary Section */}
      {filteredContracts.some(c => c.risk_score && c.risk_score > 60) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <h3 className="text-lg font-semibold text-red-900">High Risk Contracts Require Attention</h3>
          </div>
          <p className="mt-2 text-sm text-red-700">
            {stats.highRiskCount} contract{stats.highRiskCount !== 1 ? 's' : ''} have been identified with high risk scores.
            Review these contracts immediately to address potential issues.
          </p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Upload Contract Documents</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <DocumentUpload
                onUpload={handleUploadFiles}
                acceptedFormats={['.pdf', '.doc', '.docx', '.txt', '.rtf']}
                maxFileSize={25}
                maxFiles={5}
                projectId={selectedProject !== 'all' ? selectedProject : undefined}
                contractId={selectedContractId || undefined}
              />
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Contract Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Create New Contract</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project ID * <span className="text-xs text-gray-500">(Enter valid project UUID)</span>
                  </label>
                  <input
                    type="text"
                    value={newContract.project_id}
                    onChange={(e) => setNewContract({...newContract, project_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter project ID (UUID)"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Name *
                  </label>
                  <input
                    type="text"
                    value={newContract.name}
                    onChange={(e) => setNewContract({...newContract, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter contract name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Type *
                  </label>
                  <select
                    value={newContract.contract_type}
                    onChange={(e) => setNewContract({...newContract, contract_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Select type</option>
                    <option value="Construction">Construction</option>
                    <option value="Subcontract">Subcontract</option>
                    <option value="Design-Build">Design-Build</option>
                    <option value="Supply">Supply</option>
                    <option value="Service">Service</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Party A Name *
                    </label>
                    <input
                      type="text"
                      value={newContract.party_a_name}
                      onChange={(e) => setNewContract({...newContract, party_a_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="First party name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Party B Name *
                    </label>
                    <input
                      type="text"
                      value={newContract.party_b_name}
                      onChange={(e) => setNewContract({...newContract, party_b_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Second party name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Value
                  </label>
                  <input
                    type="number"
                    value={newContract.contract_value}
                    onChange={(e) => setNewContract({...newContract, contract_value: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={newContract.start_date}
                      onChange={(e) => setNewContract({...newContract, start_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={newContract.end_date}
                      onChange={(e) => setNewContract({...newContract, end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </form>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateContract}
                disabled={loading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Contract'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Contract Modal */}
      {showViewModal && viewingContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold text-gray-900">Contract Details</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setViewingContract(null)
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Contract Header */}
              <div className="mb-6 pb-6 border-b">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{viewingContract.name}</h3>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>Contract ID: {viewingContract.id}</span>
                  <span>Type: {viewingContract.contract_type}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    viewingContract.status === 'active' ? 'bg-green-100 text-green-800' :
                    viewingContract.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                    viewingContract.status === 'expired' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {viewingContract.status}
                  </span>
                </div>
              </div>

              {/* Contract Document Preview */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4">Contract Document</h4>
                <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 font-mono text-sm">
                  <div className="whitespace-pre-wrap">
{`CONSTRUCTION CONTRACT AGREEMENT

Contract Number: ${viewingContract.id}
Contract Name: ${viewingContract.name}
Contract Type: ${viewingContract.contract_type}
Date: ${new Date().toLocaleDateString()}

==================================================
PARTIES TO THE AGREEMENT
==================================================

Party A (Contractor):
${viewingContract.parties?.[0] || 'ABC Construction Inc.'}
Address: 123 Builder Lane, Construction City, ST 12345
License #: CON-2024-1234
Contact: John Smith, Project Manager
Email: john.smith@abcconstruction.com
Phone: (555) 123-4567

Party B (Owner):
${viewingContract.parties?.[1] || 'XYZ Property Owners'}
Address: 456 Property Ave, Real Estate City, ST 67890
Contact: Jane Doe, Property Owner
Email: jane.doe@xyzproperties.com
Phone: (555) 987-6543

==================================================
PROJECT DETAILS
==================================================

Project Name: ${viewingContract.project_name || 'Downtown Tower Development'}
Project Location: ${viewingContract.location || '789 Main Street, Metro City, ST 11111'}
Project Type: Commercial Construction
Total Square Footage: 50,000 sq ft
Number of Floors: 12

==================================================
CONTRACT VALUE AND PAYMENT TERMS
==================================================

Total Contract Value: ${formatCurrency(viewingContract.value)}
Payment Schedule:
- 20% upon contract signing
- 30% upon foundation completion
- 30% upon structure completion
- 15% upon interior finishing
- 5% upon final inspection and approval

Payment Terms: Net 30 days from invoice date
Late Payment Interest: 1.5% per month

==================================================
SCOPE OF WORK
==================================================

This agreement covers the complete construction services including:

1. SITE PREPARATION
   - Demolition of existing structures
   - Site excavation and grading
   - Installation of temporary facilities
   - Environmental protection measures

2. FOUNDATION WORK
   - Soil testing and preparation
   - Foundation excavation
   - Concrete footings and foundation walls
   - Waterproofing and drainage systems

3. STRUCTURAL CONSTRUCTION
   - Steel frame erection
   - Concrete floor slabs
   - Exterior wall systems
   - Roof structure and covering

4. MECHANICAL, ELECTRICAL, PLUMBING (MEP)
   - HVAC system installation
   - Electrical distribution and lighting
   - Plumbing and fixtures
   - Fire protection systems

5. INTERIOR FINISHING
   - Drywall and partitions
   - Flooring installation
   - Painting and wall finishes
   - Doors and hardware

6. EXTERNAL WORK
   - Landscaping
   - Parking areas
   - Sidewalks and external lighting
   - Signage

==================================================
TIMELINE AND MILESTONES
==================================================

Project Start Date: ${viewingContract.start_date ? new Date(viewingContract.start_date).toLocaleDateString() : 'March 1, 2024'}
Project End Date: ${viewingContract.end_date ? new Date(viewingContract.end_date).toLocaleDateString() : 'December 31, 2024'}

Key Milestones:
- Site Preparation Complete: April 15, 2024
- Foundation Complete: May 31, 2024
- Structure Complete: August 31, 2024
- MEP Systems Complete: October 15, 2024
- Interior Finishing Complete: November 30, 2024
- Final Inspection: December 15, 2024

==================================================
TERMS AND CONDITIONS
==================================================

1. CHANGES AND MODIFICATIONS
   All changes must be documented in writing and approved by both parties.

2. WARRANTIES
   Contractor warrants all work for a period of one (1) year from completion.

3. INSURANCE REQUIREMENTS
   - General Liability: Minimum $2,000,000
   - Workers' Compensation: As required by state law
   - Builder's Risk: Full replacement value

4. PERFORMANCE BOND
   Required - 10% of total contract value

5. DISPUTE RESOLUTION
   Any disputes shall first attempt resolution through mediation.

6. TERMINATION
   Either party may terminate with 30 days written notice.

7. FORCE MAJEURE
   Neither party liable for delays due to circumstances beyond control.

==================================================
COMPLIANCE AND RISK ASSESSMENT
==================================================

Risk Level: ${getRiskLabel(viewingContract.risk_score)}
Compliance Score: ${viewingContract.compliance_score || 92}%
Last Compliance Check: ${new Date().toLocaleDateString()}

Key Risk Factors:
- Payment terms: LOW RISK
- Timeline feasibility: MEDIUM RISK
- Regulatory compliance: LOW RISK
- Subcontractor management: MEDIUM RISK

==================================================
SIGNATURES
==================================================

CONTRACTOR:                        OWNER:

_________________________         _________________________
Authorized Signature              Authorized Signature

_________________________         _________________________
Print Name                        Print Name

_________________________         _________________________
Title                            Title

_________________________         _________________________
Date                             Date

==================================================
ATTACHMENTS
==================================================

A. Detailed Project Plans and Specifications
B. Payment Schedule Breakdown
C. Insurance Certificates
D. Licenses and Permits
E. Subcontractor List

This is a sample contract document for demonstration purposes.
Actual contracts should be reviewed by legal counsel before signing.`}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center">
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleDownloadContract(viewingContract)}
                    className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Contract
                  </button>
                  <button
                    className="flex items-center px-4 py-2 bg-white text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Contract
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setViewingContract(null)
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}